import React, { useEffect, useRef, useState } from 'react';
import ResellerInquiryForm from '../ResellerInquiryForm';
import LightboxSimple from '../LightboxSimple';
import { translate } from '../../Services/translation';
import ShoppingListButton from '../ShoppingListButton';
import { numberFormat } from '../../Services/Formatting.service';

const parsePrice = (price) => {
  if (typeof price === 'number') return price;
  if (!price) return 0;
  const cleaned = price.toString().replace(/[^0-9.,]/g, '').replace(',', '.');
  return parseFloat(cleaned) || 0;
};

const ConfiguratorSummary = ({
  selectedProduct,
  options = [],
  selectedFilters = {},
  selectedAccessories = [],
  moveFocusOnOpen,
  onBackToConfig,
  onReset,
  channelId,
  pageId,
  inquiryInfo,
  tosText,
}) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const titleRef = useRef(null);

  // The finish button unmounts with the configurator, so keyboard focus moves to the summary heading.
  useEffect(() => {
    if (moveFocusOnOpen) {
      titleRef.current?.focus();
    }
  }, []);
  const hideShoppingList = window.__litium.constants.hideShoppingList;

  const findValueTitle = (option, fieldValue) =>
    option.values.find((value) => value.fieldValue === fieldValue)?.title;

  const configuratorItems = [
    ...(selectedProduct ? [{ productId: selectedProduct.id, quantity: 1 }] : []),
    ...selectedAccessories.map(({ accessory, quantity }) => ({
      productId: accessory.id,
      quantity: quantity,
    })),
  ];
  const shoppingListItems = [
    ...(selectedProduct
      ? [
          {
            productId: selectedProduct.id,
            product: selectedProduct,
            quantity: 1,
          },
        ]
      : []),
    ...selectedAccessories.map(({ accessory, quantity }) => ({
      productId: accessory.id,
      product: accessory,
      quantity,
    })),
  ];

  const productPrice = parsePrice(selectedProduct?.price);
  const accessoriesTotalPrice = selectedAccessories.reduce(
    (sum, { accessory, quantity }) => sum + parsePrice(accessory?.price) * (quantity || 1),
    0
  );
  const totalPrice = productPrice + accessoriesTotalPrice;

  const accessGroups = [
    ...(selectedProduct?.portalAccessGroup ? [selectedProduct.portalAccessGroup] : []),
    ...selectedAccessories.map(a => a.accessory.portalAccessGroup).filter(Boolean)
  ].filter((x, i, a) => a.indexOf(x) === i);

  return (
    <div className="configurator__summary">
      <div className="configurator__summary-action-row">
        <button
          type="button"
          onClick={onBackToConfig}
          className="configurator__button-back"
        >
          {translate('brenderup.configurator.button.back.to.configuration')}
        </button>
        <button
          type="button"
          className="configurator__button-print"
          onClick={() => window.print()}
        >
          {translate('brenderup.configurator.button.print')}
        </button>
      </div>

      <div className="configurator__summary-container">
        <div className="configurator__summary-form-container columns">
          <h2 ref={titleRef} tabIndex={-1}>{translate('brenderup.configurator.summary.title')}</h2>
          <ResellerInquiryForm
            items={configuratorItems}
            channelId={channelId}
            pageId={pageId}
            information={inquiryInfo}
            tosText={tosText}
            accessGroups={accessGroups}
            totalPrice={totalPrice}
            analyticsEventName="sendWishlist"
            analyticsListName="configurator"
            isConfigurator={true}
            onResetConfigurator={onReset}
          />
        </div>

        <div className="configurator__summary-spec-container columns">
          {selectedProduct && (
            <div className="configurator__summary-selected-product">
              {selectedProduct.imageUrl && (
                <div className="configurator__summary-selected-product-img mobile">
                  <img src={selectedProduct.imageUrl} alt={selectedProduct.title} />
                  <button
                    type="button"
                    className="configurator__summary-lightbox-btn"
                    onClick={() => setIsLightboxOpen(true)}
                    aria-label="Show image full screen"
                  >
                    <span className="icon-zoom" />
                  </button>
                </div>
              )}
              <h3 className="name">{selectedProduct.name}</h3>
              <div className="info">
                <p className="art-nr">{translate('brenderup.configurator.product.art.nr')} {selectedProduct.id}</p>
                <p className="price">{selectedProduct.formattedPrice}</p>
              </div>
              <div className="description-container">
                <p className="description">{selectedProduct.description}</p>
              </div>
            </div>
          )}

          <ul>
            {Object.entries(selectedFilters).map(([fieldId, fieldValue]) => {
              const option = options.find((o) => o.fieldId === fieldId);
              if (!option) return null;
              return (
                <li key={fieldId}>
                  <strong>{option.title}</strong> {findValueTitle(option, fieldValue)}
                </li>
              );
            })}
          </ul>

          <p className="configurator__selected-accessories-text">
            {translate('brenderup.configurator.selected.accessories')} ({selectedAccessories.length})
          </p>

          {selectedAccessories.length === 0 ? (
            <p className="configurator__selected-accessories-text">
              {translate('brenderup.configurator.summary.no.accessories.selected')}
            </p>
          ) : (
            <div className="configurator__selected-accessories-list">
              {selectedAccessories.map(({ accessory, quantity }) => {
                const unitPrice = accessory.price ?? 0;
                const totalPrice = unitPrice * quantity;
                const formattedTotalPrice = `${numberFormat.format(totalPrice)} ${accessory.currencyCode}`;

                return (
                  <div className="configurator__selected-accessories-item" key={accessory.id}>
                    <p className="info">
                      <span className="name">{quantity} x {accessory.name}</span>
                      <span className="price">{formattedTotalPrice}</span>
                    </p>
                    <p className="art-nr">
                      {translate('brenderup.configurator.product.art.nr')} {accessory.id} {translate('brenderup.configurator.unit.price.prefix')} {accessory.formattedPrice || accessory.price}
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          <div className="configurator__summary-total-price">
            <p className="bold">{translate('brenderup.configurator.price.total')}</p>
            <p>{numberFormat.format(totalPrice)} {selectedProduct.currencyCode}</p>
          </div>
          <div className="shoppinglist-button">
            <ShoppingListButton
              productId={selectedProduct.id}
              isSmall={false}
              product={selectedProduct}
              items={shoppingListItems}
              analyticsListName="configurator"
            ></ShoppingListButton>
            <div className='shoppinglist-tooltip'>{translate('tooltip.addtowishlist')}</div>
          </div>

          {selectedProduct?.imageUrl && (
            <div className="configurator__summary-selected-product-img desktop">
              <img src={selectedProduct.imageUrl} alt={selectedProduct.title} />
              <button
                type="button"
                className="configurator__summary-lightbox-btn"
                onClick={() => setIsLightboxOpen(true)}
                aria-label="Show image full screen"
              >
                <span className="icon-zoom" />
              </button>
            </div>
          )}
        </div>
      </div>
      <LightboxSimple
        isOpen={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        imageUrl={selectedProduct?.imageUrl}
        altText={selectedProduct?.title || selectedProduct?.name}
      />
    </div>
  );
};

export default ConfiguratorSummary;